<template>
  <div class="h5-factory-header">
    <template v-if="factory">
      <div class="h5-factory-header__header">
        <div class="h5-factory-header__info">
          <div class="h5-factory-header__titles">
            <p class="h5-factory-header__title">
              {{ factory.supplierName }}
            </p>
            <Tooltip
              v-if="factory.currentUserReport"
              content="There are user reports, please choose carefully."
              trigger="click"
            >
              <Icon
                class="h5-factory-header__warning"
                name="icon-warning-outline"
              />
            </Tooltip>
          </div>
          <div class="h5-factory-header__stars">
            <img
              src="@/assets/illus/illus-supplier-trusted.svg"
              alt=""
            >
            <p class="h5-factory-header__rating">
              Credit Rating
            </p>
            <template v-if="factory.supplierRatingVo">
              <StarRate
                class="h5-factory-header__star"
                :value="factory.supplierRatingVo.compositeService / 5"
                :count="1"
              />
              <p class="h5-factory-header__score">
                {{ factory.supplierRatingVo.compositeService.toFixed(1) }}
                <span>/ 5</span>
              </p>
            </template>
            <template v-else>
              -
            </template>
          </div>
        </div>
        <Button
          class="h5-factory-header__button"
          :class="{
            'h5-factory-header__button--followed': isFollowed
          }"
          @click="onFollowClick"
          :loading="following"
          type="primary"
        >
          <template v-if="isFollowed">
            <Icon name="right-l" />
            Followed
          </template>
          <template v-else>
            <Icon name="add-l" />
            Follow
          </template>
        </Button>
      </div>
      <div class="h5-factory-header__tags">
        <p
          class="h5-factory-header__tag"
          v-for="(tag, index) in factory.tagNameList"
          :key="index"
        >
          {{ tag }}
        </p>
      </div>
      <div class="h5-factory-header__meta">
        <Icon
          class="h5-factory-header__location"
          name="location-l"
        />
        <p>{{ factory.location ?? '-' }},</p>
        <img
          class="h5-factory-header__country"
          src="https://cdn-image-old.bestdealer.com/cn.svg"
        >
        <span>China</span>
        <div
          class="h5-factory-header__dot"
        />
        <span>{{ factory.honestMerchant ?? '-' }} years</span>
        <div class="h5-factory-header__dot" />
        <span>{{ factory.employeesDesc ?? '-' }} employees</span>
        <div class="h5-factory-header__dot" />
        <span>{{ factory.plantAreaDesc ?? '-' }}</span>
        <span
          v-if="!factory.currentUserReport"
          class="h5-factory-header__report"
          @click="openReportDialog"
        >Report</span>
      </div>
      <div class="h5-factory-header-card">
        <div class="h5-factory-header-card__item">
          <p class="h5-factory-header-card__value">
            {{ factory.supplierRatingVo ? `${Math.ceil(factory.supplierRatingVo.repeatPurchasePercent * 100)}%` : '-' }}
          </p>
          <p class="h5-factory-header-card__desc">
            Last 90 days
          </p>
          <p class="h5-factory-header-card__label">
            Customer reorder rate
          </p>
        </div>
        <div class="h5-factory-header-card__item">
          <p class="h5-factory-header-card__value">
            {{ factory.customerCount ? formatSalesVolume(factory.customerCount) : '-' }}
          </p>
          <p class="h5-factory-header-card__desc">
            Total
          </p>
          <p class="h5-factory-header-card__label">
            Store followers
          </p>
        </div>
        <div class="h5-factory-header-card__item">
          <p class="h5-factory-header-card__value">
            {{ factory.soldOut ? formatAmount(factory.soldOut) : '-' }}
          </p>
          <p class="h5-factory-header-card__desc">
            Last 90 days
          </p>
          <p class="h5-factory-header-card__label">
            Number of paid orders
          </p>
        </div>
      </div>
    </template>
    <H5FactoryHeaderSkeleton v-else />
    <Popup
      v-model:show="reportDialogVisible"
      teleport="body"
      position="bottom"
      height="100%"
    >
      <Report
        @close="reportDialogVisible = false"
        :target-id="factoryId"
        type="SUPPLIER"
      />
    </Popup>
  </div>
</template>

<script setup>
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { StarRate } from '@/components/StarRate'
import { useFactory } from '@/components/SupplierHeader/useFactory'
import { formatSalesVolume, formatAmount, buriedPointUtil } from '@/utils/utils'
import H5FactoryHeaderSkeleton from './H5FactoryHeaderSkeleton.vue'
import { Popup } from '@/components/Popup'
import Report from '@/views/OfferView/components/OfferViewH5/components/Report.vue'
import { Tooltip } from '@/components/Tooltip'

const route = useRoute()

const factoryId = computed(() => route.params.id)

const { factory, isFollowed, toggleFollow } = useFactory(factoryId)

const following = ref(false)

const onFollowClick = async () => {
  following.value = true
  await toggleFollow('Top')
  following.value = false
}

const reportDialogVisible = ref(false)

const openReportDialog = () => {
  buriedPointUtil({
    eventId: 'HPD206',
    others: {
      type: 'Factory',
      objectId: factoryId.value
    }
  })
  reportDialogVisible.value = true
}

onMounted(() => {
  buriedPointUtil({ eventId: 'HPD205' })
})
</script>

<style lang="less" scoped>
.h5-factory-header {
  padding: 4px 12px 12px;
  background: linear-gradient(180deg, #B0C7F5 0%, #F8F8F8 100%);
  color: #232323;

  &__header {
    display: flex;
    gap: 12px;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__titles {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__title {
    font-family: mon-600;
    font-size: 14px;
    line-height: 18px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__warning {
    color: #DB7E25;
  }

  &__stars {
    margin-top: 4px;
    display: flex;
    align-items: center;
  }

  &__rating {
    margin-left: 8px;
    font-family: mon-500;
    font-size: 11px;
    line-height: 14px;
  }

  &__star {
    margin-left: 4px;
  }

  &__score {
    margin-left: 2px;
    font-family: mon-500;
    font-size: 11px;
    line-height: 14px;

    > span {
      color: #6E717A;
    }
  }

  &__tags {
    margin-top: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  &__tag {
    padding: 3px 6px;
    background: #B0C7F5;
    font-family: mon-600;
    font-size: 10px;
    line-height: 14px;
  }

  &__button {
    flex-shrink: 0;
    gap: 2px;
    height: 28px;
    width: 94px;
    font-size: 12px;
    line-height: 16px;

    > svg {
      font-size: 14px;
    }

    &--followed {
      background-color: #EBEBEB;
      color: #232323;
    }
  }

  &__meta {
    margin-top: 10px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    font-family: mon-500;
    font-size: 10px;
    line-height: 14px;
  }

  &__location {
    font-size: 14px;
  }

  &__country {
    margin-left: 6px;
    margin-right: 4px;
    width: 16px;
    height: 16px;
  }

  &__dot {
    margin: 0 4px;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background-color: #969AA3;
  }

  &__report {
    margin: 0 8px;
    text-decoration: underline;
    color: #6E717A;
    cursor: pointer;
  }

  &-card {
    margin-top: 10px;
    display: flex;
    gap: 24px;
    padding: 12px;
    border-radius: 8px;
    background-color: #FFFFFF;

    &__item {
      flex: 1;
      width: 0;
    }

    &__value {
      font-family: mon-700;
      font-size: 20px;
      line-height: 28px;
    }

    &__desc {
      font-family: mon-500;
      font-size: 10px;
      line-height: 14px;
      color: #6E717A;
    }

    &__label {
      margin-top: 8px;
      font-family: mon-500;
      font-size: 11px;
      line-height: 14px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
